import {
  getWorkspaceFileTree,
  invalidateWorkspaceFileTreeCache,
  getWebsocketEnabled,
  setWebsocketEnabled as apiSetWebsocketEnabled,
  getAutoContextFetchEnabled,
  setAutoContextFetchEnabled as apiSetAutoContextFetchEnabled,
  getExecutionStrategy as apiGetExecutionStrategy,
  setExecutionStrategy as apiSetExecutionStrategy,
  getYoloModeEnabled,
  setYoloModeEnabled as apiSetYoloModeEnabled,
  getYoloModeMessage,
  setYoloModeMessage as apiSetYoloModeMessage,
  getStreamingEnabled,
  setStreamingEnabled as apiSetStreamingEnabled,
  getToolsEnabled,
  setToolsEnabled as apiSetToolsEnabled,
  getSyntaxValidationEnabled,
  setSyntaxValidationEnabled as apiSetSyntaxValidationEnabled,
  getFollowupTokenLimit,
  setFollowupTokenLimit as apiSetFollowupTokenLimit,
  getHistoryCompressionEnabled,
  setHistoryCompressionEnabled as apiSetHistoryCompressionEnabled,
  getLlmRetryEnabled,
  setLlmRetryEnabled as apiSetLlmRetryEnabled,
  getLlmRetryMaxAttempts,
  setLlmRetryMaxAttempts as apiSetLlmRetryMaxAttempts,
  getNotificationsEnabled,
  setNotificationsEnabled as apiSetNotificationsEnabled,
  getEffectiveSettings,
  toggleDiscardedBatch as apiToggleDiscardedBatch,
} from '@/lib/api';
import { toast } from 'sonner';
import {
  SessionState,
  ActiveMainPage,
  UiSlice,
  ExecutionStrategyType,
  DiscardFilterMode,
  LastDiscardAction,
} from './state';
import { StateCreator } from 'zustand';

export const createUiSlice: StateCreator<SessionState, [], [], UiSlice> = (
  set,
  get,
) => ({
  activeMainPage: 'home' as ActiveMainPage,
  fileTree: [],
  isWorkspaceLoading: false,
  adHocFiles: [],
  adHocFolders: [],
  adHocCommandOutputs: [],
  executionStrategy: 'apply_revert',
  websocketEnabled: true,
  autoContextFetchEnabled: true,
  contextTokenLimit: 500000, // Default 500k
  yoloModeEnabled: false,
  yoloModeMessage: 'yolo',
  streamingEnabled: true,
  toolsEnabled: true,
  syntaxValidationEnabled: true,
  followupTokenLimit: 10000, // Default 10k
  historyCompressionEnabled: true,
  llmRetryEnabled: true,
  llmRetryMaxAttempts: 3,
  notificationsEnabled: true,
  // Discard/selection state
  discardSelectionMode: false,
  selectedInputIds: [],
  discardFilterMode: 'in_context',
  lastDiscardAction: null,
  lastSelectedInputId: null,
  orderedInputIds: [],
  // Auto-scroll state
  autoScrollEnabled: false,

  setExecutionStrategy: async (strategy: ExecutionStrategyType) => {
    const originalStrategy = get().executionStrategy;
    set({ executionStrategy: strategy });
    try {
      await apiSetExecutionStrategy(strategy);
      toast.success(
        `Execution strategy set to "${strategy.replace(/_/g, ' ')}".`,
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update execution strategy.';
      toast.error(errorMessage);
      // Revert UI state on failure
      set({ executionStrategy: originalStrategy });
    }
  },

  setActiveMainPage: (page: ActiveMainPage) => set({ activeMainPage: page }),
  goBackToHome: () => set({ activeMainPage: 'home' }),

  setAdHocFiles: (files: string[] | ((prev: string[]) => string[])) =>
    set((state) => ({
      adHocFiles: typeof files === 'function' ? files(state.adHocFiles) : files,
    })),
  setAdHocFolders: (folders: string[] | ((prev: string[]) => string[])) =>
    set((state) => ({
      adHocFolders:
        typeof folders === 'function'
          ? folders(state.adHocFolders)
          : folders,
    })),
  setAdHocCommandOutputs: (
    outputs: string[] | ((prev: string[]) => string[]),
  ) =>
    set((state) => ({
      adHocCommandOutputs:
        typeof outputs === 'function'
          ? outputs(state.adHocCommandOutputs)
          : outputs,
    })),

  addAdHocContext: ({
    files = [],
    folders = [],
  }: {
    files?: string[];
    folders?: string[];
  }) => {
    set((state) => ({
      adHocFiles: [...new Set([...state.adHocFiles, ...files])],
      adHocFolders: [...new Set([...state.adHocFolders, ...folders])],
    }));
    toast.success(
      `${files.length} file(s) and ${folders.length} folder(s) added to ad-hoc context.`,
    );
  },

  addAdHocCommandOutput: (output: string) => {
    set((state) => ({
      adHocCommandOutputs: [
        ...new Set([...state.adHocCommandOutputs, output]),
      ],
    }));
    toast.success('Command output added to ad-hoc context.');
  },

  clearAdHocContext: () => {
    set({ adHocFiles: [], adHocFolders: [], adHocCommandOutputs: [] });
  },

  fetchWebsocketEnabled: async () => {
    try {
      const { enabled } = await getWebsocketEnabled();
      set({ websocketEnabled: enabled });
    } catch (err) {
      console.error('Failed to fetch websocket setting:', err);
    }
  },

  setWebsocketEnabled: async (enabled: boolean) => {
    set({ websocketEnabled: enabled });
    try {
      await apiSetWebsocketEnabled(enabled);
      toast.success(`Websocket messaging ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update websocket setting.';
      toast.error(errorMessage);
      // Revert UI state on failure
      set((state) => ({ websocketEnabled: !state.websocketEnabled }));
    }
  },

  fetchAutoContextFetchEnabled: async () => {
    try {
      const { enabled } = await getAutoContextFetchEnabled();
      set({ autoContextFetchEnabled: enabled });
    } catch (err) {
      console.error('Failed to fetch auto-context-fetch setting:', err);
    }
  },

  setAutoContextFetchEnabled: async (enabled: boolean) => {
    set({ autoContextFetchEnabled: enabled });
    try {
      await apiSetAutoContextFetchEnabled(enabled);
      toast.success(`Auto Context Fetch ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update auto-context-fetch setting.';
      toast.error(errorMessage);
      // Revert UI state on failure
      set((state) => ({
        autoContextFetchEnabled: !state.autoContextFetchEnabled,
      }));
    }
  },

  fetchContextTokenLimit: async () => {
    try {
      const { limit } = await getContextTokenLimit();
      set({ contextTokenLimit: limit });
    } catch (err) {
      console.error('Failed to fetch context token limit:', err);
    }
  },

  setContextTokenLimit: async (limit: number) => {
    const originalLimit = get().contextTokenLimit;
    set({ contextTokenLimit: limit });
    try {
      await apiSetContextTokenLimit(limit);
      toast.success(
        `Context token limit set to ${limit.toLocaleString()}.`,
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update context token limit.';
      toast.error(errorMessage);
      set({ contextTokenLimit: originalLimit });
    }
  },

  fetchExecutionStrategy: async () => {
    try {
      const { strategy } = await apiGetExecutionStrategy();
      set({ executionStrategy: strategy as ExecutionStrategyType });
    } catch (err) {
      console.error('Failed to fetch execution strategy setting:', err);
    }
  },

  fetchYoloModeEnabled: async () => {
    try {
      const { enabled } = await getYoloModeEnabled();
      set({ yoloModeEnabled: enabled });
    } catch (err) {
      console.error('Failed to fetch yolo mode setting:', err);
    }
  },

  setYoloModeEnabled: async (enabled: boolean) => {
    const originalValue = get().yoloModeEnabled;
    set({ yoloModeEnabled: enabled });
    try {
      await apiSetYoloModeEnabled(enabled);
      toast.success(`YOLO mode ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update yolo mode setting.';
      toast.error(errorMessage);
      set({ yoloModeEnabled: originalValue }); // Revert on failure
    }
  },

  fetchYoloModeMessage: async () => {
    try {
      const { message } = await getYoloModeMessage();
      set({ yoloModeMessage: message });
    } catch (err) {
      console.error('Failed to fetch yolo mode message:', err);
    }
  },

  setYoloModeMessage: async (message: string) => {
    const originalMessage = get().yoloModeMessage;
    set({ yoloModeMessage: message });
    try {
      await apiSetYoloModeMessage(message);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update yolo mode message.';
      toast.error(errorMessage);
      set({ yoloModeMessage: originalMessage }); // Revert on failure
    }
  },

  fetchStreamingEnabled: async () => {
    try {
      const { enabled } = await getStreamingEnabled();
      set({ streamingEnabled: enabled });
    } catch (err) {
      console.error('Failed to fetch streaming setting:', err);
    }
  },

  setStreamingEnabled: async (enabled: boolean) => {
    const originalValue = get().streamingEnabled;
    set({ streamingEnabled: enabled });
    try {
      await apiSetStreamingEnabled(enabled);
      toast.success(`Streaming ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update streaming setting.';
      toast.error(errorMessage);
      set({ streamingEnabled: originalValue }); // Revert on failure
    }
  },

  fetchToolsEnabled: async () => {
    try {
      const { enabled } = await getToolsEnabled();
      set({ toolsEnabled: enabled });
    } catch (err) {
      console.error('Failed to fetch tools enabled setting:', err);
    }
  },

  setToolsEnabled: async (enabled: boolean) => {
    const originalValue = get().toolsEnabled;
    set({ toolsEnabled: enabled });
    try {
      await apiSetToolsEnabled(enabled);
      toast.success(`Tools ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update tools enabled setting.';
      toast.error(errorMessage);
      set({ toolsEnabled: originalValue }); // Revert on failure
    }
  },

  fetchSyntaxValidationEnabled: async () => {
    try {
      const { enabled } = await getSyntaxValidationEnabled();
      set({ syntaxValidationEnabled: enabled });
    } catch (err) {
      console.error('Failed to fetch syntax validation setting:', err);
    }
  },

  setSyntaxValidationEnabled: async (enabled: boolean) => {
    const originalValue = get().syntaxValidationEnabled;
    set({ syntaxValidationEnabled: enabled });
    try {
      await apiSetSyntaxValidationEnabled(enabled);
      toast.success(
        `Syntax validation ${enabled ? 'enabled' : 'disabled'}.`,
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update syntax validation setting.';
      toast.error(errorMessage);
      set({ syntaxValidationEnabled: originalValue }); // Revert on failure
    }
  },
fetchFollowupTokenLimit: async () => {
    try {
      const { limit } = await getFollowupTokenLimit();
      set({ followupTokenLimit: limit });
    } catch (err) {
      console.error('Failed to fetch followup token limit:', err);
    }
  },

  setFollowupTokenLimit: async (limit: number) => {
    const originalLimit = get().followupTokenLimit;
    set({ followupTokenLimit: limit });
    try {
      await apiSetFollowupTokenLimit(limit);
      toast.success(
        `Follow-up token limit set to ${limit.toLocaleString()}.`,
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update followup token limit.';
      toast.error(errorMessage);
      set({ followupTokenLimit: originalLimit }); // Revert on failure
    }
  },

  fetchHistoryCompressionEnabled: async () => {
    try {
      const { enabled } = await getHistoryCompressionEnabled();
      set({ historyCompressionEnabled: enabled });
    } catch (err) {
      console.error('Failed to fetch history compression setting:', err);
    }
  },

  setHistoryCompressionEnabled: async (enabled: boolean) => {
    const originalValue = get().historyCompressionEnabled;
    set({ historyCompressionEnabled: enabled });
    try {
      await apiSetHistoryCompressionEnabled(enabled);
      toast.success(
        `History compression ${enabled ? 'enabled' : 'disabled'}.`,
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update history compression setting.';
      toast.error(errorMessage);
      set({ historyCompressionEnabled: originalValue }); // Revert on failure
    }
  },

  fetchLlmRetryEnabled: async () => {
    try {
      const { enabled } = await getLlmRetryEnabled();
      set({ llmRetryEnabled: enabled });
    } catch (err) {
      console.error('Failed to fetch LLM retry enabled setting:', err);
    }
  },

  setLlmRetryEnabled: async (enabled: boolean) => {
    const originalValue = get().llmRetryEnabled;
    set({ llmRetryEnabled: enabled });
    try {
      await apiSetLlmRetryEnabled(enabled);
      toast.success(`LLM retry ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update LLM retry setting.';
      toast.error(errorMessage);
      set({ llmRetryEnabled: originalValue }); // Revert on failure
    }
  },

  fetchLlmRetryMaxAttempts: async () => {
    try {
      const { attempts } = await getLlmRetryMaxAttempts();
      set({ llmRetryMaxAttempts: attempts });
    } catch (err) {
      console.error('Failed to fetch LLM retry max attempts:', err);
    }
  },

  setLlmRetryMaxAttempts: async (attempts: number) => {
    const originalValue = get().llmRetryMaxAttempts;
    set({ llmRetryMaxAttempts: attempts });
    try {
      await apiSetLlmRetryMaxAttempts(attempts);
      toast.success(`LLM retry max attempts set to ${attempts}.`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update LLM retry max attempts.';
      toast.error(errorMessage);
      set({ llmRetryMaxAttempts: originalValue }); // Revert on failure
    }
  },

  fetchNotificationsEnabled: async () => {
    try {
      const { enabled } = await getNotificationsEnabled();
      set({ notificationsEnabled: enabled });
    } catch (err) {
      console.error('Failed to fetch notifications setting:', err);
    }
  },

  setNotificationsEnabled: async (enabled: boolean) => {
    const originalValue = get().notificationsEnabled;
    set({ notificationsEnabled: enabled });
    try {
      await apiSetNotificationsEnabled(enabled);
      toast.success(`Notifications ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to update notifications setting.';
      toast.error(errorMessage);
      set({ notificationsEnabled: originalValue }); // Revert on failure
    }
  },

  // Batch fetch all application state settings in one API call
  fetchAllApplicationState: async () => {
    try {
      const settings = await getEffectiveSettings();
      set({
        websocketEnabled: settings.websocketEnabled.value,
        autoContextFetchEnabled: settings.autoContextFetchEnabled.value,
        contextTokenLimit: settings.contextTokenLimit.value,
        yoloModeEnabled: settings.yoloModeEnabled.value,
        yoloModeMessage: settings.yoloModeMessage.value,
        streamingEnabled: settings.streamingEnabled.value,
        toolsEnabled: settings.toolsEnabled.value,
        syntaxValidationEnabled: settings.syntaxValidationEnabled.value,
        followupTokenLimit: settings.followupTokenLimit.value,
        historyCompressionEnabled: settings.historyCompressionEnabled.value,
        llmRetryEnabled: settings.llmRetryEnabled.value,
        llmRetryMaxAttempts: settings.llmRetryMaxAttempts.value,
        notificationsEnabled: settings.notificationsEnabled.value,
        executionStrategy: settings.executionStrategy.value as ExecutionStrategyType,
      });
    } catch (err) {
      console.error('Failed to fetch application state:', err);
    }
  },

  fetchWorkspaceFileTree: async () => {
    set({ isWorkspaceLoading: true });
    try {
      const { tree } = await getWorkspaceFileTree();
      set({
        fileTree: tree,
        isWorkspaceLoading: false,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch workspace file tree.';
      set({ isWorkspaceLoading: false, error: errorMessage });
      toast.error(errorMessage);
    }
  },

  invalidateAndFetchWorkspaceFileTree: async () => {
    set({ isWorkspaceLoading: true });
    try {
      await invalidateWorkspaceFileTreeCache();
      toast.success('File tree cache invalidated. Refreshing...');
      await get().fetchWorkspaceFileTree(); // This will set loading to false on completion
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to invalidate file tree cache.';
      set({ isWorkspaceLoading: false, error: errorMessage });
      toast.error(errorMessage);
    }
  },

  // Discard/selection actions
  setDiscardSelectionMode: (mode: boolean) => {
    set({ discardSelectionMode: mode, selectedInputIds: [], lastSelectedInputId: null });
  },

  toggleInputSelection: (inputId: string) => {
    set((state) => {
      const isSelected = state.selectedInputIds.includes(inputId);
      return {
        selectedInputIds: isSelected
          ? state.selectedInputIds.filter((id) => id !== inputId)
          : [...state.selectedInputIds, inputId],
        lastSelectedInputId: inputId,  // Track anchor for range selection
      };
    });
  },

  selectRange: (fromId: string, toId: string) => {
    set((state) => {
      const fromIndex = state.orderedInputIds.indexOf(fromId);
      const toIndex = state.orderedInputIds.indexOf(toId);
      
      if (fromIndex === -1 || toIndex === -1) {
        return state;  // IDs not found in ordered list
      }
      
      const rangeIds = state.orderedInputIds.slice(
        Math.min(fromIndex, toIndex),
        Math.max(fromIndex, toIndex) + 1
      );
      
      return {
        selectedInputIds: [...new Set([...state.selectedInputIds, ...rangeIds])],
        lastSelectedInputId: toId,
      };
    });
  },

  setOrderedInputIds: (ids: string[]) => {
    set({ orderedInputIds: ids });
  },

  selectAllInSection: (inputIds: string[]) => {
    set((state) => {
      // Toggle: if all are selected, deselect all; otherwise, select all
      const allSelected = inputIds.every((id) => state.selectedInputIds.includes(id));
      return {
        selectedInputIds: allSelected
          ? state.selectedInputIds.filter((id) => !inputIds.includes(id))
          : [...new Set([...state.selectedInputIds, ...inputIds])],
      };
    });
  },

  clearSelection: () => {
    set({ selectedInputIds: [], lastSelectedInputId: null });
  },

  setDiscardFilterMode: (mode: DiscardFilterMode) => {
    set({ discardFilterMode: mode });
  },

  toggleDiscardedBatch: async (sessionId: string, is_discarded: boolean) => {
    const selectedIds = get().selectedInputIds;
    if (selectedIds.length === 0) return;

    // Store previous state for undo
    set({ lastDiscardAction: { input_ids: selectedIds, is_discarded: !is_discarded } });

    try {
      await apiToggleDiscardedBatch(sessionId, {
        input_ids: selectedIds,
        is_discarded,
      });
      toast.success(
        `${selectedIds.length} message(s) ${is_discarded ? 'discarded from' : 'restored to'} context.`,
      );
      set({ selectedInputIds: [] });
      // Refresh session details to update is_discarded flags
      const { fetchSessionDetails } = get() as any;
      if (fetchSessionDetails) {
        await fetchSessionDetails(sessionId, 'refresh');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to toggle discarded status.';
      toast.error(errorMessage);
      set({ lastDiscardAction: null });
    }
  },

  undoLastDiscardAction: async (sessionId: string) => {
    const lastAction = get().lastDiscardAction;
    if (!lastAction) return;

    try {
      await apiToggleDiscardedBatch(sessionId, {
        input_ids: lastAction.input_ids,
        is_discarded: lastAction.is_discarded,
      });
      toast.success('Undo successful.');
      set({ lastDiscardAction: null });
      const { fetchSessionDetails } = get() as any;
      if (fetchSessionDetails) {
        await fetchSessionDetails(sessionId, 'refresh');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to undo discard action.';
      toast.error(errorMessage);
    }
  },

  setAutoScrollEnabled: (enabled: boolean) => {
    set({ autoScrollEnabled: enabled });
  },
});